import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { authApi, type AdminUserProfile } from '../api/auth.api.js';
import {
  ApiError,
  getAccessToken,
  LEGACY_SESSION_STORAGE_KEY,
  renewSession,
  setAccessToken,
  setRefreshHook,
} from '../api/client.js';

import { can as roleCan, type Capability } from './capabilities.js';

export interface AuthState {
  user: AdminUserProfile | null;
  loading: boolean;
}

export interface AuthContextValue extends AuthState {
  login: (email: string, password: string, totpCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** What the signed-in role may do (DEV-67 matrix, auth/capabilities.ts). */
  can: (capability: Capability) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Before DEV-125 the access AND refresh tokens sat in localStorage, readable by
 * any script on the admin origin. Nothing is stored now: the refresh token is
 * an HttpOnly cookie, the access token lives in memory, and a reloaded page
 * restores the session from the cookie. A record left by an older build is
 * removed unread; its owner signs in once more.
 */
function dropLegacySession(): void {
  try {
    window.localStorage.removeItem(LEGACY_SESSION_STORAGE_KEY);
  } catch {
    // Storage may be unavailable (private mode); there is then nothing to drop.
  }
}

/** Rejected credentials end the session; an outage, a deploy or a 5xx do not. */
function isRefusal(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const queryClient = useQueryClient();
  const sessionGeneration = useRef(0);
  const userRef = useRef<AdminUserProfile | null>(null);
  // Loading until the first renewal from the cookie has answered, so a
  // protected screen neither flashes the login page nor fires bare requests.
  const [state, setState] = useState<AuthState>({ user: null, loading: true });

  const showUser = useCallback((user: AdminUserProfile | null): void => {
    userRef.current = user;
    setState({ user, loading: false });
  }, []);

  const endSession = useCallback((): void => {
    setAccessToken(null);
    queryClient.clear();
    showUser(null);
  }, [queryClient, showUser]);

  // Register the refresh hook with the API client. On page load and on a 401
  // the client calls this to renew from the cookie; null means the session is
  // over and the client sends the browser to /login.
  useEffect(() => {
    setRefreshHook(async (): Promise<string | null> => {
      const generation = sessionGeneration.current;
      try {
        const result = await authApi.refresh();
        if (generation !== sessionGeneration.current) {
          // An old renewal must not restore an administrator after this
          // browser signed out or switched to another session. It may also
          // have rotated the old admin's cookie into this browser just now:
          // revoke and clear it, so a reload cannot bring that admin back.
          void authApi.logout().catch(() => undefined);
          return getAccessToken();
        }
        setAccessToken(result.accessToken);
        if (userRef.current !== null && userRef.current.role !== result.user.role) queryClient.clear();
        showUser(result.user);
        return result.accessToken;
      } catch (error: unknown) {
        if (generation !== sessionGeneration.current) return getAccessToken();
        if (!isRefusal(error)) throw error;
        endSession();
        return null;
      }
    });
    return (): void => setRefreshHook(null);
  }, [queryClient, showUser, endSession]);

  // Restore the session from the HttpOnly cookie once, on page load.
  useEffect(() => {
    dropLegacySession();
    const generation = sessionGeneration.current;
    renewSession().catch(() => null).then((token) => {
      // A sign-in that finished first owns the state now.
      if (generation !== sessionGeneration.current) return;
      if (token === null) showUser(null);
      else setState((current) => (current.loading ? { ...current, loading: false } : current));
    }, () => undefined);
  }, [showUser]);

  const login = useCallback(
    async (email: string, password: string, totpCode?: string): Promise<void> => {
      const generation = ++sessionGeneration.current;
      const result = await authApi.login(email, password, totpCode);
      if (generation !== sessionGeneration.current) return;
      queryClient.clear();
      setAccessToken(result.accessToken);
      showUser(result.user);
    },
    [queryClient, showUser],
  );

  const logout = useCallback(async (): Promise<void> => {
    ++sessionGeneration.current;
    // The gateway revokes the refresh cookie's token and clears the cookie.
    const pending = authApi.logout();
    endSession();
    try { await pending; } catch { /* best-effort */ }
  }, [endSession]);

  const can = useCallback(
    (capability: Capability): boolean => roleCan(state.user?.role, capability),
    [state.user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, can }),
    [state, login, logout, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
